
import { useState, useContext, useEffect } from "react";
import * as XLSX from "xlsx";
import { IncidentDrivenTestTable, IncidentTestCase } from "@/components/IncidentDrivenTestTable";
import UploadModal from "@/components/UploadModal";
import { usePinnedFile } from "@/contexts/PinnedFileContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Cpu, MemoryStick, HardDrive, Database, Zap } from "lucide-react";
import { StressTabKPIs, StressOverviewKPIs } from "@/components/StressTabKPIs";

// Types for CPU info
interface CpuInfo {
  name: string;
  cores: number;
  logicalCpus: number;
  maxClock: number;
  usage: number;
}

interface CpuUsageHistory {
  before: number;
  during: number;
  after: number;
}

// ...existing code...

// Quick Actions for each tab
const cartsIncidentAction = {
  label: "View CARTS Incidents",
  description: "Open the CARTS incident management system.",
  link: "https://carts.yourdomain.com/incidents" // Replace with actual URL
};

const quickActions = {
  overview: [
    { label: "Run All Stress Tests", description: "Execute all scenarios in sequence." },
    { label: "Export Results", description: "Download a summary report." },
    cartsIncidentAction
  ],
  cpu: [
    { label: "Start CPU Test", description: "Begin CPU stress scenario." },
    { label: "Analyze CPU Bottlenecks", description: "Get recommendations for CPU issues." },
    cartsIncidentAction
  ],
  memory: [
    { label: "Start Memory Test", description: "Begin memory stress scenario." },
    { label: "Analyze Memory Usage", description: "Get recommendations for memory issues." },
    cartsIncidentAction
  ],
  io: [
    { label: "Start I/O Test", description: "Begin I/O stress scenario." },
    { label: "Analyze Disk Latency", description: "Get recommendations for I/O issues." },
    cartsIncidentAction
  ],
  tempdb: [
    { label: "Start TempDB Test", description: "Begin TempDB stress scenario." },
    { label: "Analyze TempDB Usage", description: "Get recommendations for TempDB issues." },
    cartsIncidentAction
  ]
};

export default function StressTestingPage() {
  // All hooks and handlers must be inside this function
  const [cpuInfo, setCpuInfo] = useState<CpuInfo | null>(null);
  const [cpuUsageHistory, setCpuUsageHistory] = useState<CpuUsageHistory | null>(null);
  const [burning, setBurning] = useState(false);
  const [burnError, setBurnError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [rightPaneTab, setRightPaneTab] = useState("quick");
  const { pinnedFiles, pinFile } = usePinnedFile();
  const [uploadModalOpen, setUploadModalOpen] = useState<false | 'overview' | 'carts-incidents'>(false);
  const [incidentRows, setIncidentRows] = useState<IncidentTestCase[]>([]);
  const [incidentUploadError, setIncidentUploadError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/cpu-info")
      .then(res => res.json())
      .then(setCpuInfo)
      .catch(() => setCpuInfo(null));
  }, []);

  const handleStartCpuBurn = async (threads: number, duration: number) => {
    setBurning(true);
    setBurnError(null);
    setCpuUsageHistory(null);
    try {
      const res = await fetch("/api/cpu-burn", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ threads, duration })
      });
      if (!res.ok) throw new Error("Failed to start CPU burn");
      const data = await res.json();
      setCpuUsageHistory(data);
    } catch (err: any) {
      setBurnError(err.message || "Failed to start CPU burn");
    }
    setBurning(false);
  };

  const handleIncidentCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json: Record<string, string>[] = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
        const rows: IncidentTestCase[] = json.map((row) => {
          const closeNotes = row["Close notes"] || "";
          return {
            incidentIds: row["Number"] || "",
            symptoms: (row["Title"] || "") + (closeNotes.includes("Issue:") ? "\n" + closeNotes.split("Issue:")[1].split("\n")[0] : ""),
            scenario: (row["Title"] || "") + (closeNotes.includes("Description of issue") ? "\n" + closeNotes.split("Description of issue")[1].split("\n")[0] : ""),
            stressSetup: closeNotes.match(/blocking|bridge call|index|processes killed|latency|deadlock|timeout|RFID|spiking|delay|error/i)?.[0] || "",
            metrics: closeNotes.match(/blocking|deadlock|latency|index|processes killed|wait|timeout|RFID|spiking|delay|error/i)?.[0] || "",
            why: (closeNotes.match(/improvement|no longer seeing latency|user|impact|system|resolved|closing|monitor|follow up|support|complaints|order processing|instability|failed|data loss|support tickets/i)?.[0] || "")
          };
        });
        setIncidentRows(rows);
        setIncidentUploadError(null);
      } catch (err) {
        setIncidentUploadError("Failed to parse incidents file.");
      }
    };
    reader.onerror = () => setIncidentUploadError("Failed to read file.");
    reader.readAsArrayBuffer(file);
  };

  return (
    <div className="relative flex flex-row max-w-7xl mx-auto px-4 py-8">
      {/* Main Content */}
      <div className="flex-1 pr-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          SQL Server Stress Testing
        </h1>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Simulate, monitor, and analyze SQL Server and infrastructure stress scenarios.
        </p>
        <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5 mb-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="cpu">CPU</TabsTrigger>
            <TabsTrigger value="memory">Memory</TabsTrigger>
            <TabsTrigger value="io">I/O</TabsTrigger>
            <TabsTrigger value="tempdb">TempDB</TabsTrigger>
          </TabsList>
          {/* Each tab gets its own right-hand pane */}
          <TabsContent value="overview" className="space-y-6 flex flex-row">
            <div className="flex-1">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-red-600" />
                    Stress Testing Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-gray-700 dark:text-gray-200 space-y-2">
                    <p>
                      <b>Welcome to SQL Server Stress Testing.</b> Use this page to simulate, monitor, and analyze how your SQL Server and infrastructure respond under extreme load conditions.
                    </p>
                    <ul className="list-disc pl-6 text-sm mb-4">
                      <li>Review <b>Success Criteria</b> above to understand performance goals.</li>
                      <li>Use the tabs to run targeted stress tests for <b>CPU</b>, <b>Memory</b>, <b>I/O</b>, and <b>TempDB</b>.</li>
                      <li>Analyze results and compare actuals to targets.</li>
                      <li>Ask Copilot questions in the Agent box for guidance or troubleshooting.</li>
                    </ul>
                    {/* Overview of all scenario KPIs */}
                    <StressOverviewKPIs />
                  </div>
                </CardContent>
              </Card>
            </div>
            {/* Right-hand Quick Actions Pane for Overview */}
            <aside className="w-80 min-w-[320px] max-w-xs border-l border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 fixed right-0 top-[74px] h-[calc(100vh-74px)] z-30 shadow-lg flex flex-col">
              <div className="flex-1 overflow-y-auto">
                <h2 className="text-xl font-bold text-shiftleft-900 mb-2">Quick Actions</h2>
                <div className="grid grid-cols-1 gap-4">
                  {quickActions['overview'].map((action, idx) => {
                    if (action.label === 'Run All Stress Tests') {
                      const pinned = pinnedFiles['overview'];
                      return (
                        <Card key={action.label} className="w-full">
                          <CardHeader className="flex flex-row items-center gap-3 pb-2">
                            <CardTitle className="text-base font-semibold flex-1">{action.label}</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="text-sm text-gray-700 mb-2">{action.description}</div>
                            <button
                              className="w-full bg-shiftleft-600 text-white py-1.5 px-4 rounded-lg hover:bg-shiftleft-700 transition-colors text-sm font-medium"
                              onClick={() => {
                                if (pinned) {
                                  alert(`Running stress tests with pinned file: ${pinned.name}`);
                                } else {
                                  setUploadModalOpen('overview');
                                }
                              }}
                            >
                              {pinned ? `Run with: ${pinned.name}` : 'Upload File to Run'}
                            </button>
                          </CardContent>
                        </Card>
                      );
                    }
                    if (action.label === 'View CARTS Incidents') {
                      const pinned = pinnedFiles['carts-incidents'];
                      return (
                        <Card key={action.label} className="w-full">
                          <CardHeader className="flex flex-row items-center gap-3 pb-2">
                            <CardTitle className="text-base font-semibold flex-1">{action.label}</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="text-sm text-gray-700 mb-2">{action.description}</div>
                            <button
                              className="w-full bg-shiftleft-600 text-white py-1.5 px-4 rounded-lg hover:bg-shiftleft-700 transition-colors text-sm font-medium"
                              onClick={() => {
                                if (pinned) {
                                  alert(`Opening pinned CARTS incidents file: ${pinned.name}`);
                                } else {
                                  setUploadModalOpen('carts-incidents');
                                }
                              }}
                            >
                              {pinned ? `Open: ${pinned.name}` : 'Upload Incidents File'}
                            </button>
                          </CardContent>
                        </Card>
                      );
                    }
                    return (
                      <Card key={action.label} className="w-full">
                        <CardHeader className="flex flex-row items-center gap-3 pb-2">
                          <CardTitle className="text-base font-semibold flex-1">{action.label}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-sm text-gray-700 mb-2">{action.description}</div>
                          {('link' in action && action.link) ? (
                            <a
                              href={action.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full inline-block bg-shiftleft-600 text-white py-1.5 px-4 rounded-lg hover:bg-shiftleft-700 transition-colors text-sm font-medium text-center"
                            >
                              Go to CARTS
                            </a>
                          ) : (
                            <button className="w-full bg-shiftleft-600 text-white py-1.5 px-4 rounded-lg hover:bg-shiftleft-700 transition-colors text-sm font-medium" disabled>
                              Coming Soon
                            </button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                  {uploadModalOpen && (
                    <UploadModal
                      clientCode={""}
                      isOpen={!!uploadModalOpen}
                      onClose={() => setUploadModalOpen(false)}
                      context={
                        uploadModalOpen === 'carts-incidents'
                          ? {
                              page: 'carts-incidents',
                              title: 'Upload CARTS Incidents File',
                              description: 'Upload a file to use for the CARTS incidents quick action.',
                              expectedFileTypes: ['csv', 'xlsx', 'json'],
                              analysisType: 'CARTS Incidents',
                              handler: 'cartsIncidentsUpload',
                              instructions: [
                                'Upload your incidents file (CSV, XLSX, or JSON)',
                                'The system will process and make it available for quick access.'
                              ]
                            }
                          : {
                              page: 'overview',
                              title: 'Upload File for Stress Test',
                              description: 'Upload a file to use for the general stress test.',
                              expectedFileTypes: ['pdf', 'docx', 'xlsx', 'png', 'jpg', 'json', 'xml', 'sql'],
                              analysisType: 'General File Analysis',
                              handler: 'generalUpload',
                              instructions: [
                                'Upload files for general analysis',
                                'The system will attempt to:',
                                '• Detect file type and content',
                                '• Extract relevant information',
                                '• Provide analysis recommendations'
                              ]
                            }
                      }
                      onUploadSuccess={(files: any[]) => {
                        if (files && files.length > 0) {
                          if (uploadModalOpen === 'carts-incidents') {
                            pinFile('carts-incidents', files[0]);
                          } else {
                            pinFile('overview', files[0]);
                          }
                          setUploadModalOpen(false);
                        }
                      }}
                    />
                  )}
                </div>
              </div>
            </aside>
          </TabsContent>
          {/* Repeat for other tabs: cpu, memory, io, tempdb */}
          <TabsContent value="cpu" className="space-y-6 flex flex-row">
            <div className="flex-1">
              <StressTabKPIs type="cpu" />
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Cpu className="w-5 h-5 text-orange-600" />
                    CPU Stress Testing
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* CPU stress scenario configuration UI goes here */}
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold mb-2">CPU Stress Test Cases</h3>
                    <table className="min-w-full border text-xs">
                    {/* CPU Info and Burn Controls */}
                    <div className="mb-4 p-4 border rounded bg-gray-50 dark:bg-gray-900">
                      <h4 className="font-semibold mb-2">CPU Info</h4>
                      {cpuInfo ? (
                        <ul className="text-xs mb-2">
                          <li><b>Name:</b> {cpuInfo.name}</li>
                          <li><b>Cores:</b> {cpuInfo.cores}</li>
                          <li><b>Logical CPUs:</b> {cpuInfo.logicalCpus}</li>
                          <li><b>Max Clock:</b> {cpuInfo.maxClock} MHz</li>
                          <li><b>Current Usage:</b> {cpuInfo.usage}%</li>
                        </ul>
                      ) : (
                        <div className="text-xs text-gray-500">Loading CPU info...</div>
                      )}
                      <form className="flex flex-row gap-2 items-end mb-2" onSubmit={e => {
                        e.preventDefault();
                        const threads = Number((e.currentTarget.elements.namedItem('threads') as HTMLInputElement).value) || 1;
                        const duration = Number((e.currentTarget.elements.namedItem('duration') as HTMLInputElement).value) || 10;
                        handleStartCpuBurn(threads, duration);
                      }}>
                        <div>
                          <label className="block text-xs font-medium">Threads</label>
                          <input name="threads" type="number" min="1" defaultValue={cpuInfo?.cores || 1} className="border rounded px-2 py-1 w-16 text-xs" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium">Duration (sec)</label>
                          <input name="duration" type="number" min="1" defaultValue={10} className="border rounded px-2 py-1 w-20 text-xs" />
                        </div>
                        <button type="submit" className="bg-shiftleft-600 text-white px-3 py-1 rounded text-xs font-semibold disabled:opacity-60" disabled={burning}>
                          {burning ? "Running..." : "Start CPU Stress Test"}
                        </button>
                      </form>
                      {burnError && <div className="text-xs text-red-600">{burnError}</div>}
                      {cpuUsageHistory && (
                        <div className="text-xs mt-2">
                          <b>CPU Usage:</b> Before: {cpuUsageHistory.before}% | During: {cpuUsageHistory.during}% | After: {cpuUsageHistory.after}%
                        </div>
                      )}
                    </div>
                    <div className="flex justify-end mb-2">
                      <button
                        className="bg-shiftleft-600 text-white px-4 py-2 rounded hover:bg-shiftleft-700 font-semibold text-sm"
                        onClick={() => {
                          // Placeholder: Replace with actual stress test trigger logic
                          alert('CPU Stress Test started!');
                        }}
                      >
                        Start CPU Stress Test
                      </button>
                    </div>
                      <thead className="bg-gray-100 dark:bg-gray-800">
                        <tr>
                          <th className="border px-2 py-1">Test Case</th>
                          <th className="border px-2 py-1">Related Incidents</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { label: "Max Parallel Query Execution (simulate high concurrency)", keywords: ["parallel", "concurrency", "multiple sessions", "high concurrency"] },
                          { label: "Long-running Query Loop (CPU-bound T-SQL workload)", keywords: ["long-running", "loop", "duration"] },
                          { label: "Scalar Function Stress (repeated scalar UDF calls)", keywords: ["scalar function", "udf"] },
                          { label: "Complex Join/Sort Operations (large in-memory joins/sorts)", keywords: ["join", "sort", "in-memory"] },
                          { label: "Parallel Index Rebuilds (multiple indexes at once)", keywords: ["index rebuild", "index"] },
                          { label: "Batch Insert/Update Operations (bulk DML stress)", keywords: ["batch insert", "bulk dml", "update"] },
                          { label: "Temp Table/CTE Recursion (recursive CTEs/temp tables)", keywords: ["temp table", "cte", "recursion"] },
                          { label: "CLR Integration Workload (CPU-intensive .NET code in SQL)", keywords: ["clr", ".net"] },
                          { label: "Ad Hoc Query Flood (randomized query patterns)", keywords: ["ad hoc", "randomized query"] },
                          { label: "Plan Cache Thrashing (frequent recompiles/option changes)", keywords: ["plan cache", "recompile", "option change"] },
                        ].map((test, idx) => {
                          // Find related incidents by keyword match in scenario/symptoms/metrics
                          const related = incidentRows
                            .filter(inc => test.keywords.some(k =>
                              (inc.scenario + " " + inc.symptoms + " " + inc.metrics).toLowerCase().includes(k)
                            ))
                            .map(inc => inc.incidentIds)
                            .filter(Boolean)
                            .join(", ");
                          return (
                            <tr key={idx}>
                              <td className="border px-2 py-1 whitespace-pre-line">{test.label}</td>
                              <td className="border px-2 py-1 whitespace-pre-line text-shiftleft-700">{related || "-"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold mb-2">Incident-Driven, Multi-Angle Test Table</h3>
                    <input type="file" accept=".csv" onChange={handleIncidentCsvUpload} className="mb-2" />
                    {incidentUploadError && <div className="text-red-600 text-xs mb-2">{incidentUploadError}</div>}
                    {incidentRows.length > 0 && <IncidentDrivenTestTable incidents={incidentRows} />}
                  </div>
                </CardContent>
              </Card>
            </div>
            {/* Right-hand Quick Actions Pane for CPU */}
            <aside className="w-80 min-w-[320px] max-w-xs border-l border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 fixed right-0 top-[74px] h-[calc(100vh-74px)] z-30 shadow-lg flex flex-col">
              <div className="flex-1 overflow-y-auto">
                <h2 className="text-xl font-bold text-shiftleft-900 mb-2">Quick Actions</h2>
                <div className="grid grid-cols-1 gap-4">
                  {quickActions['cpu'].map((action, idx) => {
                    // You can add CPU-specific logic here
                    return (
                      <Card key={action.label} className="w-full">
                        <CardHeader className="flex flex-row items-center gap-3 pb-2">
                          <CardTitle className="text-base font-semibold flex-1">{action.label}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-sm text-gray-700 mb-2">{action.description}</div>
                          {('link' in action && action.link) ? (
                            <a
                              href={action.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full inline-block bg-shiftleft-600 text-white py-1.5 px-4 rounded-lg hover:bg-shiftleft-700 transition-colors text-sm font-medium text-center"
                            >
                              Go to CARTS
                            </a>
                          ) : (
                            <button className="w-full bg-shiftleft-600 text-white py-1.5 px-4 rounded-lg hover:bg-shiftleft-700 transition-colors text-sm font-medium" disabled>
                              Coming Soon
                            </button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </aside>
          </TabsContent>
          {/* Repeat for memory, io, tempdb with similar structure */}
        </Tabs>
      </div>
    </div>
  );
}

